import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "@/lib/api";
import { ago, day } from "@/lib/dates";
import { Badge, Button, Card, ConfirmButton, Empty, ErrorText, Field, Input, Section } from "@/ui/kit";
import { PageHeader } from "@/ui/Shell";

interface Row { id: string; firstName: string | null; lastName: string | null; email: string; avatarColor: string; status: string; role: string; neighborhoodName: string | null; cohortName: string | null; lastActiveAt: string | null }

const Avatar = ({ f, l, c, big }: { f: string | null; l: string | null; c: string; big?: boolean }) => (
  <span className={`grid shrink-0 place-items-center rounded-full font-bold ${big ? "size-16 text-xl" : "size-11"}`} style={{ background: c }}>
    {`${f?.[0] ?? ""}${l?.[0] ?? ""}`.toUpperCase() || "?"}
  </span>
);

export function Members() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const r = useQuery({ queryKey: ["members", dq], queryFn: () => api<Row[]>(`/v1/admin/members?q=${encodeURIComponent(dq)}`) });
  return (
    <>
      <PageHeader title="People" />
      <Input placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search members" className="mb-3" />
      <ErrorText error={r.error} />
      <div className="grid gap-2">
        {(r.data ?? []).map((m) => (
          <Card key={m.id} onClick={() => nav(`/members/${m.id}`)} className="flex items-center gap-3">
            <Avatar f={m.firstName} l={m.lastName} c={m.avatarColor} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{[m.firstName, m.lastName].filter(Boolean).join(" ") || "(not onboarded)"}</p>
              <p className="truncate text-sm text-muted">{[m.neighborhoodName, m.cohortName].filter(Boolean).join(" · ") || m.email}</p>
            </div>
            {m.status !== "active" ? <Badge tone="blush">{m.status}</Badge> : m.role === "admin" ? <Badge tone="ink">admin</Badge> : null}
          </Card>
        ))}
      </div>
      {r.isSuccess && !r.data.length ? <Empty title="No one matches that search" /> : null}
    </>
  );
}

interface Detail {
  id: string; name: string; firstName: string | null; lastName: string | null; email: string; avatarColor: string; status: string; role: string; neighborhoodName: string | null;
  createdAt: string; lastActiveAt: string | null; kidsAges: string[]; cohorts: { id: string; name: string }[]; vouchCount: number;
  balance: { balance: number; pendingDebit: number; pendingCredit: number };
  ledger: { id: string; deltaHours: number; reason: string; createdAt: string; title: string | null }[];
  reports: { id: string; reason: string; details: string; status: string; createdAt: string; aboutThem: boolean; other: string }[];
}

export function MemberDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["member", id], queryFn: () => api<Detail>(`/v1/admin/members/${id}`) });
  const [hours, setHours] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const m = q.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["member", id] });

  if (!m) return <><PageHeader title="Member" back="/members" /><ErrorText error={q.error} /></>;
  return (
    <>
      <PageHeader title={m.name} back="/members" />
      <div className="mb-4 flex items-center gap-4">
        <Avatar f={m.firstName} l={m.lastName} c={m.avatarColor} big />
        <div className="min-w-0">
          <p className="truncate text-sm">{m.email}</p>
          <p className="text-sm text-muted">{m.neighborhoodName ?? "No neighborhood"} · joined {day(m.createdAt)}</p>
          <p className="text-sm text-muted">{m.lastActiveAt ? `Active ${ago(m.lastActiveAt)}` : "Not active yet"}</p>
        </div>
      </div>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {m.status !== "active" ? <Badge tone="blush">{m.status}</Badge> : null}
        {m.cohorts.map((c) => <Badge key={c.id}>{c.name}</Badge>)}
        <Badge tone="sage">{m.vouchCount} vouches</Badge>
        {m.kidsAges.map((a, i) => <Badge key={i} tone="butter">Kid · {a}</Badge>)}
      </div>

      <Section title="Co-op hours" action={<Link to={`/coop?status=all&user=${id}&name=${encodeURIComponent([m.firstName, m.lastName].filter(Boolean).join(" "))}`} className="text-sm font-semibold underline underline-offset-2">Her requests & offers →</Link>}>
        <div className="mb-3 grid grid-cols-3 gap-2 text-center">
          {[["Balance", m.balance.balance], ["Sitting soon", m.balance.pendingCredit], ["Requested", m.balance.pendingDebit]].map(([l, n]) => (
            <div key={l as string} className="rounded-2xl bg-mist p-3"><p className="text-xl font-bold">{(n as number) > 0 && l === "Balance" ? "+" : ""}{n}</p><p className="text-xs text-muted">{l}</p></div>
          ))}
        </div>
        <Card>
          <p className="mb-2 font-semibold">Correct hours</p>
          <div className="grid grid-cols-[7rem_1fr] gap-2">
            <Field label="Hours (±)">{(i) => <Input id={i} inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="+2.5" />}</Field>
            <Field label="Reason">{(i) => <Input id={i} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Sat at the cohort event" />}</Field>
          </div>
          <ErrorText error={error} />
          <Button
            variant="secondary"
            className="w-full"
            loading={busy}
            disabled={!Number(hours) || reason.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api(`/v1/admin/members/${id}/hours`, { method: "POST", body: { hours: Number(hours), reason } });
                setHours("");
                setReason("");
                await refresh();
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save correction
          </Button>
        </Card>
        {m.ledger.length ? (
          <ul className="mt-3 divide-y divide-line rounded-2xl border border-line bg-white">
            {m.ledger.map((l) => (
              <li key={l.id} className="flex items-center gap-3 px-4 py-3">
                <span className="flex-1 text-sm">{l.title ?? l.reason.replace("admin_adjustment: ", "Admin: ")}<span className="block text-xs text-muted">{day(l.createdAt)}</span></span>
                <span className={`font-bold ${l.deltaHours >= 0 ? "text-ink" : "text-rose-deep"}`}>{l.deltaHours > 0 ? "+" : ""}{l.deltaHours}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </Section>

      {m.reports.length ? (
        <Section title="Reports">
          <div className="grid gap-2">
            {m.reports.map((r) => (
              <Card key={r.id}>
                <div className="mb-1 flex gap-2"><Badge tone={r.aboutThem ? "blush" : "mist"}>{r.aboutThem ? `From ${r.other}` : `About ${r.other}`}</Badge><Badge>{r.reason}</Badge><Badge tone="sage">{r.status}</Badge></div>
                {r.details ? <p className="text-sm">“{r.details}”</p> : null}
                <p className="text-xs text-muted">{day(r.createdAt)}</p>
              </Card>
            ))}
          </div>
        </Section>
      ) : null}

      {m.role !== "admin" ? (
        <Section title="Account">
          {m.status === "suspended" ? (
            <Button variant="secondary" className="w-full" onClick={async () => { await api(`/v1/admin/members/${id}/unsuspend`, { method: "POST" }); await refresh(); }}>Restore access</Button>
          ) : (
            <>
              <p className="mb-2 text-sm text-muted">Suspending signs her out everywhere immediately and withdraws her open sitting requests and offers.</p>
              <ConfirmButton label="Suspend account" confirmLabel="Yes, suspend" onConfirm={async () => { await api(`/v1/admin/members/${id}/suspend`, { method: "POST" }); await refresh(); }} className="w-full" />
            </>
          )}
        </Section>
      ) : null}
    </>
  );
}
